import React, { useContext, useEffect } from 'react'
import { Navigate, RouteProps } from 'react-router-dom'
import { useLocation } from 'react-use'
import { Button } from 'src/components/atoms'
import Loading from 'src/components/templates/Loading'
import { AuthContext } from 'src/context/auth.context'
import useAuthorizationGuidance from 'src/hooks/useAuthorizationGuidance'
import Sidebar from 'src/pages/Sidebar'
import { pushPathStack } from 'src/slices/navigation/navigationSlice'
import { useAppDispatch, useAppSelector } from 'src/store'
import { SIDEBAR_WIDTH } from 'src/styles/constants'
import Path from './path'

type PrivateRouteProps = {
  showSidebar?: boolean
} & RouteProps

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  showSidebar,
}: PrivateRouteProps) => {
  const authContext = useContext(AuthContext)
  const dispatch = useAppDispatch()
  const location = useLocation()

  const { token, status, currentAuthorization } = useAppSelector(
    (state) => state.auth
  )
  const { currentRoot } = useAppSelector((state) => state.navigation)
  const guidance = useAuthorizationGuidance()

  useEffect(() => {
    // current がない場合、店舗一覧の path をセットする
    if (currentAuthorization && !currentRoot) {
      dispatch(
        pushPathStack(Path.stores(currentAuthorization.organization.code))
      )
    }
    if (location.pathname) {
      dispatch(pushPathStack(location.pathname))
    }
  }, [currentAuthorization, currentRoot, dispatch, location.pathname])

  const renderPageContent = (): React.ReactNode => {
    if (!currentAuthorization && (status === 'idle' || status === 'loading')) {
      return <Loading />
    }
    if (currentAuthorization?.active) {
      return children
    }
    return (
      <div style={{ padding: '20px' }}>
        <h2>この画面を表示できませんでした</h2>
        <p>
          お手数ですが、ページを再読み込みしてください。
          <br />
          解消しない場合、アカウントのステータスが利用停止になっているか、アカウントの登録が完了していない可能性があります。
          <br />
          お手数ですが、ログイン中のメールアドレスを添えて、管理者までご連絡ください。
        </p>
        <Button
          color="primary"
          size="large"
          title="ページを再読み込み"
          onClick={() => window.location.reload()}
        />
      </div>
    )
  }

  // リロードしたときにFirebase Authでログイン中でも、auth.userの初期値がnullなので、
  // リダイレクトしてしまうのを防ぐため
  if (authContext.initializing) {
    return <Loading />
  }

  if (token) {
    // 案内画面は操作できる画面が1つも無い状態なので、ナビゲーションごと隠して
    // ログイン直後に同じ状態になったときと見え方を揃える
    if (guidance) {
      return guidance
    }
    if (showSidebar) {
      return (
        <div style={{ display: 'flex' }}>
          <Sidebar />
          <div
            style={{
              width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
            }}
          >
            {renderPageContent()}
          </div>
        </div>
      )
    }
    return children
  }
  return <Navigate replace to="/login" />
}

export default PrivateRoute
